import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Input, Segmented, Spin } from "antd";
import debounce from "lodash.debounce";
import {
  FileText,
  Folders,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { searchMattersAPI } from "../../service/mailIntake";
import { searchDocumentsAPI } from "../../service/search";
import {
  LoadMoreButton,
  ResultMeta,
  ResultRow,
  ResultTitle,
  SearchHeader,
  SearchPageWrapper,
  SearchResultsArea,
  SearchStatus,
  SearchTitle,
} from "./SearchStyled";

/**
 * Search page (Mobile DMS v1, Phase 4) — replaces the long-standing
 * `/search` stub. Works on both shells; on mobile it's a primary tab.
 *
 * Two scopes behind one input:
 *
 *   Matters   → `GET /api/mail-intake/matter-search/?q=` — the fast
 *               relevance-ranked endpoint built for the Assign screen
 *               (PR #1296). The old `/matters/?search=` path could take
 *               ~50s under fuzzy multi-word terms; never use it for
 *               typeahead.
 *   Documents → `GET /api/documents/?search=` — name / category /
 *               path search with pagination.
 *
 * Row taps navigate into the matter workspace: matters open
 * `/matter/<id>`, documents open their matter with the doc preselected
 * (`?doc=<id>` on mobile-friendly routes; the desktop MatterDetails
 * tree ignores the param harmlessly).
 */

const SCOPES = { MATTERS: "Matters", DOCUMENTS: "Documents" };
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 350;

const SearchPage = () => {
  const navigate = useNavigate();
  const [scope, setScope] = useState(SCOPES.MATTERS);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ready | failed
  const [matterResults, setMatterResults] = useState([]);
  const [docResults, setDocResults] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [docPage, setDocPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // Monotonic token so a slow response for an old query can't
  // overwrite results for the current one.
  const requestSeqRef = useRef(0);

  const runSearch = useCallback(async (term, activeScope, page = 1) => {
    const trimmed = term.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      requestSeqRef.current += 1;
      setStatus("idle");
      setMatterResults([]);
      setDocResults([]);
      setDocCount(0);
      return;
    }
    const seq = ++requestSeqRef.current;
    if (page === 1) setStatus("loading");
    else setLoadingMore(true);
    try {
      if (activeScope === SCOPES.MATTERS) {
        const response = await searchMattersAPI(trimmed);
        if (seq !== requestSeqRef.current) return;
        setMatterResults(response?.data?.results || []);
      } else {
        const response = await searchDocumentsAPI({
          query: trimmed,
          page,
          pageSize: 25,
        });
        if (seq !== requestSeqRef.current) return;
        const results = response?.data?.results || [];
        setDocResults((prev) => (page === 1 ? results : [...prev, ...results]));
        setDocCount(response?.data?.count || results.length);
        setDocPage(page);
      }
      setStatus("ready");
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error("Search failed:", err);
      setStatus("failed");
    } finally {
      if (seq === requestSeqRef.current) setLoadingMore(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((term, activeScope) => runSearch(term, activeScope), DEBOUNCE_MS),
    [runSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleQueryChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    debouncedSearch(value, scope);
  };

  const handleScopeChange = (value) => {
    setScope(value);
    // Re-run immediately with the current query — scope flips are an
    // explicit gesture, no debounce needed.
    debouncedSearch.cancel();
    runSearch(query, value);
  };

  const handleOpenMatter = (matter) => {
    if (!matter?.id) return;
    navigate(`/matter/${matter.id}`, {
      state: { matterName: matter.display_name || matter.client_name || "" },
    });
  };

  const handleOpenDocument = (doc) => {
    if (!doc?.matter) return;
    navigate(
      `/matter/${doc.matter}?doc=${encodeURIComponent(doc.id)}`,
      {
        state: {
          documentName: doc.name || "",
          documentFileType: doc.file_type || "",
        },
      }
    );
  };

  const trimmedLength = query.trim().length;
  const showResults = status === "ready";
  const activeResults =
    scope === SCOPES.MATTERS ? matterResults : docResults;

  return (
    <SearchPageWrapper>
      <SearchHeader>
        <SearchTitle>Search</SearchTitle>
        <Input
          size="large"
          allowClear
          autoFocus
          value={query}
          onChange={handleQueryChange}
          placeholder={
            scope === SCOPES.MATTERS
              ? "Search matters by client or name…"
              : "Search documents by name, category, or path…"
          }
          prefix={<MagnifyingGlass size={16} color="#7688A3" />}
          aria-label="Search"
        />
        <Segmented
          block
          options={[SCOPES.MATTERS, SCOPES.DOCUMENTS]}
          value={scope}
          onChange={handleScopeChange}
        />
      </SearchHeader>

      <SearchResultsArea>
        {status === "loading" && (
          <SearchStatus role="status">
            <Spin />
            Searching…
          </SearchStatus>
        )}

        {status === "failed" && (
          <SearchStatus role="alert">
            Search failed — check your connection and try again.
          </SearchStatus>
        )}

        {status === "idle" && trimmedLength < MIN_QUERY_LENGTH && (
          <SearchStatus role="status">
            <MagnifyingGlass size={32} weight="duotone" color="#98A6BA" />
            Type at least {MIN_QUERY_LENGTH} characters to search{" "}
            {scope === SCOPES.MATTERS ? "matters" : "documents"}.
          </SearchStatus>
        )}

        {showResults && activeResults.length === 0 && (
          <SearchStatus role="status">
            No {scope === SCOPES.MATTERS ? "matters" : "documents"} matched
            &ldquo;{query.trim()}&rdquo;.
          </SearchStatus>
        )}

        {showResults &&
          scope === SCOPES.MATTERS &&
          matterResults.map((matter) => (
            <ResultRow
              key={matter.id}
              type="button"
              onClick={() => handleOpenMatter(matter)}
            >
              <ResultTitle>
                {matter.display_name || matter.client_name || matter.id}
              </ResultTitle>
              <ResultMeta>
                <Folders
                  size={12}
                  weight="fill"
                  style={{ verticalAlign: "-2px", marginRight: 4 }}
                />
                {matter.client_name || "Matter"}
              </ResultMeta>
            </ResultRow>
          ))}

        {showResults &&
          scope === SCOPES.DOCUMENTS &&
          docResults.map((doc) => (
            <ResultRow
              key={doc.id}
              type="button"
              onClick={() => handleOpenDocument(doc)}
            >
              <ResultTitle>{doc.name || "Untitled document"}</ResultTitle>
              <ResultMeta>
                <FileText
                  size={12}
                  weight="fill"
                  style={{ verticalAlign: "-2px", marginRight: 4 }}
                />
                {[doc.category, doc.file_type?.toUpperCase()]
                  .filter(Boolean)
                  .join(" · ") || "Document"}
              </ResultMeta>
            </ResultRow>
          ))}

        {showResults &&
          scope === SCOPES.DOCUMENTS &&
          docResults.length < docCount && (
            <LoadMoreButton
              type="button"
              disabled={loadingMore}
              onClick={() => runSearch(query, scope, docPage + 1)}
            >
              {loadingMore
                ? "Loading…"
                : `Load more (${docResults.length} of ${docCount})`}
            </LoadMoreButton>
          )}
      </SearchResultsArea>
    </SearchPageWrapper>
  );
};

export default SearchPage;
